




























import { computed, defineComponent, onMounted, onUnmounted, ref, watch, watchEffect } from '@vue/composition-api';
import OfflineMapButton from '@/components/map/OfflineMapButton.vue';
import { FIELDS_MAP_ID } from '@/constants';
import { useGroups } from '@/store/modules/group';
import { useAllResources } from '@/store/modules/resources';
import debounce from '@/utils/debounce';
import FieldsList from './FieldsList.vue';

export interface FieldsListSheetProps {
	collapse: () => void;
	expand: () => void;
}

export default defineComponent({
	components: {
		FieldsList,
		OfflineMapButton,
	},
	setup(props, { root }) {
		const sheetRef = ref<HTMLElement | null>(null);
		const viewportHeight = ref<number>(0);
		const mapButtonVisible = ref<boolean>(false);
		const numberOfResults = computed(() => root.$store.getters['fieldsView/getFieldRowsLength']);
		const pointToFly = computed<[number, number] | null>(() =>
			root.$store.getters['map/getPointToFlyByMapId'](FIELDS_MAP_ID)
		);
		const { isLoading: areResourcesLoading } = useAllResources(root.$store);
		const { isLoading: areGroupsLoading } = useGroups(root.$store);
		const isLoading = computed(() => areGroupsLoading.value || areResourcesLoading.value);

		const collapse = () => {
			sheetRef.value?.parentElement?.scroll(0, 0);
		};

		const expand = () => {
			sheetRef.value?.parentElement?.scroll(0, viewportHeight.value);
		};

		const handleResize = () => {
			viewportHeight.value = sheetRef.value?.parentElement?.clientHeight ?? 0;
		};

		const resetScroll = () => {
			sheetRef.value?.parentElement?.scroll(0, viewportHeight.value / 2 - 72);
		};

		const setMapButtonVisible = () => {
			const scrollTop = sheetRef.value?.parentElement?.scrollTop ?? 0;
			mapButtonVisible.value = scrollTop > (viewportHeight.value * 3) / 4 - 72;
		};

		const debouncedSetMapButtonVisible = debounce(setMapButtonVisible, 250);

		const setMapButtonHidden = () => {
			mapButtonVisible.value = false;
		};

		// Collapse sheet if click FindMarkerButton
		watch(
			() => pointToFly.value,
			() => {
				if (pointToFly.value) {
					collapse();
				}
			}
		);

		watchEffect((cleanup) => {
			sheetRef.value?.parentElement?.addEventListener('scroll', debouncedSetMapButtonVisible);
			sheetRef.value?.parentElement?.addEventListener('scroll', setMapButtonHidden);

			cleanup(() => {
				sheetRef.value?.parentElement?.removeEventListener('scroll', debouncedSetMapButtonVisible);
				sheetRef.value?.parentElement?.removeEventListener('scroll', setMapButtonHidden);
			});
		});

		watchEffect((cleanup) => {
			window.addEventListener('resize', handleResize);

			cleanup(() => {
				window.removeEventListener('resize', handleResize);
			});
		});

		onMounted(() => {
			handleResize();
			resetScroll();
		});

		onUnmounted(() => {
			window.history.scrollRestoration = 'manual';
		});

		return {
			isLoading,
			sheetRef,
			numberOfResults,
			mapButtonVisible,
			collapse,
			expand,
		};
	},
});
